import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DatePicker } from "@/components/ui/date-picker";
import { Field, FieldLabel } from "@/components/ui/field";
import { ChevronRight, ChevronDown, Plus, Pencil, Ban, FolderTree } from "lucide-react";
import {
  useCostCentreTree,
  useCostCentres,
  useCreateCostCentre,
  useUpdateCostCentre,
  useDeactivateCostCentre,
  useCompanySummaries,
} from "../hooks/useAccounting";
import { useQuery } from "@tanstack/react-query";
import { accountingApi } from "../services/accountingApi";
import { PermissionGate } from "@/shared/components/PermissionGate";
import { useNotification } from "@/shared/hooks/useNotification";
import { getErrorMessage, getFieldErrors } from "@/shared/utils/errorHandler";
import type { CostCentreTreeDto, CreateCostCentreDto, UpdateCostCentreDto } from "../services/accountingApi";

function fmt(n: number) {
  return new Intl.NumberFormat("en-KE", { style: "currency", currency: "KES", minimumFractionDigits: 2 }).format(n);
}

// ── Tree Node ─────────────────────────────────────────────────────────────────

function CostCentreNode({
  node,
  level,
  onEdit,
  onDeactivate,
}: {
  node: CostCentreTreeDto;
  level: number;
  onEdit: (node: CostCentreTreeDto) => void;
  onDeactivate: (id: string) => void;
}) {
  const [expanded, setExpanded] = useState(true);
  const hasChildren = node.children && node.children.length > 0;

  return (
    <div>
      <div
        className={`flex items-center gap-2 py-1.5 px-2 hover:bg-muted/50 rounded-md ${!node.isActive ? "opacity-50" : ""}`}
        style={{ paddingLeft: `${level * 24 + 8}px` }}
      >
        <button className="w-5 h-5 flex items-center justify-center shrink-0" onClick={() => setExpanded(!expanded)}>
          {hasChildren ? (expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />) : <span className="w-4" />}
        </button>
        <span className="font-mono text-sm text-muted-foreground w-24 shrink-0">{node.costCentreCode}</span>
        <span className="flex-1 text-sm font-medium">{node.costCentreName}</span>
        {node.isGroup && <Badge variant="secondary" className="text-xs"><FolderTree className="h-3 w-3 mr-1" />Group</Badge>}
        <span className="text-xs text-muted-foreground w-40 shrink-0 truncate hidden md:inline">{node.companyName ?? ""}</span>
        <span className="text-xs text-muted-foreground w-44 shrink-0 truncate hidden lg:inline">{node.description ?? ""}</span>
        {!node.isActive && <Badge variant="outline" className="text-xs">Inactive</Badge>}
        <div className="flex gap-1 ml-2">
          <PermissionGate permissions={["cost_centres.update"]}>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onEdit(node)}><Pencil className="h-3.5 w-3.5" /></Button>
          </PermissionGate>
          <PermissionGate permissions={["cost_centres.deactivate"]}>
            {node.isActive && (
              <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => onDeactivate(node.id)}><Ban className="h-3.5 w-3.5" /></Button>
            )}
          </PermissionGate>
        </div>
      </div>
      {expanded && hasChildren && node.children.map((c) => (
        <CostCentreNode key={c.id} node={c} level={level + 1} onEdit={onEdit} onDeactivate={onDeactivate} />
      ))}
    </div>
  );
}

// ── Allocation Report View ────────────────────────────────────────────────────

function AllocationReport() {
  const currentYear = new Date().getFullYear();
  const [startDate, setStartDate] = useState(`${currentYear}-01-01`);
  const [endDate, setEndDate] = useState(new Date().toISOString().split("T")[0]);
  const [runToken, setRunToken] = useState(0);

  const { data, isLoading } = useQuery({
    queryKey: ["cost-centres", "allocation", startDate, endDate, runToken],
    queryFn: () => accountingApi.getCostCentreAllocationReport(startDate, endDate),
    enabled: runToken > 0,
  });

  return (
    <div className="space-y-4">
      <div className="flex items-end gap-4">
        <Field>
          <FieldLabel>Start Date</FieldLabel>
          <DatePicker value={startDate} onChange={setStartDate} />
        </Field>
        <Field>
          <FieldLabel>End Date</FieldLabel>
          <DatePicker value={endDate} onChange={setEndDate} />
        </Field>
        <Button onClick={() => setRunToken((t) => t + 1)} disabled={isLoading}>
          {isLoading ? "Loading..." : "Run Report"}
        </Button>
      </div>
      {data && data.length > 0 ? (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead><TableHead>Cost Centre</TableHead>
              <TableHead className="text-right">Debits</TableHead><TableHead className="text-right">Credits</TableHead>
              <TableHead className="text-right">Net</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((cc) => (
              <TableRow key={cc.costCentreId}>
                <TableCell className="font-mono text-sm">{cc.costCentreCode}</TableCell>
                <TableCell>{cc.costCentreName}</TableCell>
                <TableCell className="text-right font-mono text-sm">{fmt(cc.totalDebits)}</TableCell>
                <TableCell className="text-right font-mono text-sm">{fmt(cc.totalCredits)}</TableCell>
                <TableCell className="text-right font-mono text-sm font-semibold">{fmt(cc.netAmount)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : runToken > 0 && !isLoading ? (
        <div className="text-center py-8 text-muted-foreground">No allocation data for this period</div>
      ) : null}
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────

export function CostCentresPage() {
  const { data: tree, isLoading } = useCostCentreTree();
  const { data: costCentresList } = useCostCentres();
  const { data: companies } = useCompanySummaries();
  const createMutation = useCreateCostCentre();
  const updateMutation = useUpdateCostCentre();
  const deactivateMutation = useDeactivateCostCentre();
  const notify = useNotification();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [showAllocation, setShowAllocation] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});

  const [form, setForm] = useState({
    id: "",
    costCentreCode: "",
    costCentreName: "",
    parentCostCentreId: "",
    companyId: "",
    description: "",
  });

  const resetForm = () => {
    setForm({ id: "", costCentreCode: "", costCentreName: "", parentCostCentreId: "", companyId: "", description: "" });
    setFieldErrors({});
    setEditMode(false);
  };

  const openCreate = () => { resetForm(); setDialogOpen(true); };

  const openEdit = (node: CostCentreTreeDto) => {
    setForm({
      id: node.id,
      costCentreCode: node.costCentreCode,
      costCentreName: node.costCentreName,
      parentCostCentreId: "",
      companyId: node.companyId || "",
      description: node.description || "",
    });
    setEditMode(true);
    setFieldErrors({});
    setDialogOpen(true);
  };

  const handleDeactivate = (id: string) => {
    deactivateMutation.mutate(id, {
      onSuccess: () => notify.success("Cost centre deactivated"),
      onError: (err) => notify.error(getErrorMessage(err)),
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFieldErrors({});

    if (editMode) {
      const dto: UpdateCostCentreDto = {
        id: form.id,
        costCentreName: form.costCentreName,
        parentCostCentreId: form.parentCostCentreId || null,
        companyId: form.companyId || null,
        description: form.description || null,
        isGroup: false,  // managed automatically by the backend
      };
      updateMutation.mutate({ id: form.id, dto }, {
        onSuccess: () => { notify.success("Updated"); setDialogOpen(false); },
        onError: (err) => { const fe = getFieldErrors(err); if (fe) setFieldErrors(fe); notify.error(getErrorMessage(err)); },
      });
    } else {
      const dto: CreateCostCentreDto = {
        costCentreCode: form.costCentreCode,
        costCentreName: form.costCentreName,
        parentCostCentreId: form.parentCostCentreId || null,
        companyId: form.companyId || null,
        description: form.description || null,
        isGroup: false,  // managed automatically by the backend
      };
      createMutation.mutate(dto, {
        onSuccess: () => { notify.success("Created"); setDialogOpen(false); resetForm(); },
        onError: (err) => { const fe = getFieldErrors(err); if (fe) setFieldErrors(fe); notify.error(getErrorMessage(err)); },
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Cost Centres</h1>
          <p className="text-muted-foreground">Manage cost centres and view allocation reports</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowAllocation(!showAllocation)}>
            {showAllocation ? "Show Tree" : "Allocation Report"}
          </Button>
          <PermissionGate permissions={["cost_centres.create"]}>
            <Button onClick={openCreate}><Plus className="h-4 w-4 mr-2" /> New Cost Centre</Button>
          </PermissionGate>
        </div>
      </div>

      {showAllocation ? (
        <Card>
          <CardHeader><CardTitle>Cost Centre Allocation Report</CardTitle></CardHeader>
          <CardContent><AllocationReport /></CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="pt-6">
            {isLoading ? (
              <div className="text-center py-8 text-muted-foreground">Loading...</div>
            ) : !tree?.length ? (
              <div className="text-center py-8 text-muted-foreground">No cost centres created yet</div>
            ) : (
              <div className="border rounded-lg divide-y">
                {/* Column headers */}
                <div className="flex items-center gap-2 px-2 py-1.5 bg-muted/30 text-xs font-medium text-muted-foreground rounded-t-lg" style={{ paddingLeft: "8px" }}>
                  <span className="w-5 shrink-0" />
                  <span className="w-24 shrink-0">Code</span>
                  <span className="flex-1">Name</span>
                  <span className="hidden md:inline w-40 shrink-0">Company</span>
                  <span className="hidden lg:inline w-44 shrink-0">Description</span>
                  <span className="w-16 shrink-0" />
                </div>
                {tree.map((node) => (
                  <CostCentreNode key={node.id} node={node} level={0} onEdit={openEdit} onDeactivate={handleDeactivate} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>{editMode ? "Edit Cost Centre" : "New Cost Centre"}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {!editMode && (
              <Field>
                <FieldLabel>Code <span className="text-red-500">*</span></FieldLabel>
                <Input value={form.costCentreCode} onChange={(e) => setForm({ ...form, costCentreCode: e.target.value })} required />
                {fieldErrors.costCentreCode && <p className="text-red-500 text-xs mt-1">{fieldErrors.costCentreCode.join(", ")}</p>}
              </Field>
            )}
            <Field>
              <FieldLabel>Name <span className="text-red-500">*</span></FieldLabel>
              <Input value={form.costCentreName} onChange={(e) => setForm({ ...form, costCentreName: e.target.value })} required />
              {fieldErrors.costCentreName && <p className="text-red-500 text-xs mt-1">{fieldErrors.costCentreName.join(", ")}</p>}
            </Field>
            <Field>
              <FieldLabel>Company</FieldLabel>
              <Select value={form.companyId || "none"} onValueChange={(v) => setForm({ ...form, companyId: v === "none" ? "" : v, parentCostCentreId: "" })}>
                <SelectTrigger><SelectValue placeholder="Select company" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— No company —</SelectItem>
                  {companies?.filter(c => c.isActive).map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.companyName}{c.abbreviation ? ` (${c.abbreviation})` : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field>
              <FieldLabel>Parent Cost Centre</FieldLabel>
              <Select value={form.parentCostCentreId || "none"} onValueChange={(v) => setForm({ ...form, parentCostCentreId: v === "none" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="Root (no parent)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Root (no parent)</SelectItem>
                  {costCentresList
                    ?.filter((cc) => !form.companyId || !cc.companyId || cc.companyId === form.companyId)
                    .filter((cc) => cc.id !== form.id)
                    .map((cc) => (
                    <SelectItem key={cc.id} value={cc.id}>{cc.costCentreCode} — {cc.costCentreName}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field>
              <FieldLabel>Description</FieldLabel>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </Field>
            <p className="text-xs text-muted-foreground">
              A cost centre automatically becomes a <strong>group</strong> (container only, no direct postings) when another cost centre is placed under it as a child.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" type="button" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                {(createMutation.isPending || updateMutation.isPending) ? "Saving..." : editMode ? "Update" : "Create"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
